import { z } from "zod";

import { ActivityLevel, Allergen, DietGoal, DietType, KitchenEquipment, MealSlot, Shop } from "./diet";
import { WEEKDAYS } from "./gyms";
import { isAdultBirthYear } from "./legal";
import {
  KeyLift,
  Muscle,
  RepScheme,
  Sex,
  TrainingExperience,
  TrainingGoal,
  TrainingSplit,
  TrainingVariety,
  WeightUnit,
} from "./training";

export enum QuestionnaireKind {
  TRAINING = "training",
  DIET = "diet",
}

/** Body data both questionnaires lean on. Stored on `athlete_profile`, not in the answers. */
/** A birth year, saved as the first of January. Accounts are for adults, so a younger year is refused. */
const adultBirthDateSchema = z.iso.date().refine((birthDate) => isAdultBirthYear(birthDate));

export const athleteProfileSchema = z.object({
  sex: z.enum(Sex).nullable(),
  birthDate: adultBirthDateSchema.nullable(),
  heightCm: z.number().min(100).max(250).nullable(),
  weightKg: z.number().min(30).max(300).nullable(),
  weightUnit: z.enum(WeightUnit),
});
export type AthleteProfile = z.infer<typeof athleteProfileSchema>;

/**
 * The required first-run onboarding: every body field must be given before the app opens. Sex stays
 * optional: "prefer not to say" is an answer, and the engines read it as unknown.
 */
export const athleteOnboardingSchema = athleteProfileSchema.extend({
  birthDate: adultBirthDateSchema,
  heightCm: z.number().min(100).max(250),
  weightKg: z.number().min(30).max(300),
});

/** A known lift: a set of one key lift the athlete can do today, whole implement (bar included). */
export const knownLiftSchema = z.object({
  lift: z.enum(KeyLift),
  weightKg: z.number().min(1).max(500),
  reps: z.number().int().min(1).max(20),
});
export type KnownLift = z.infer<typeof knownLiftSchema>;

const range = (min: number, max: number) =>
  z
    .object({ min: z.number().int().min(min).max(max), max: z.number().int().min(min).max(max) })
    .refine((value) => value.min <= value.max, { message: "min must not exceed max" });

/**
 * The training questionnaire. Every field has a default, so a half-filled questionnaire is still
 * one the engine can generate from, and adding a field later never invalidates stored answers.
 */
export const trainingQuestionnaireSchema = z.object({
  goal: z.enum(TrainingGoal).default(TrainingGoal.MUSCLE),
  experience: z.enum(TrainingExperience).default(TrainingExperience.BEGINNER),
  sessionsPerWeek: z.number().int().min(1).max(7).default(3),
  sessionMinutes: z.number().int().min(20).max(120).default(60),
  /** The days they train, for their week and reminders. The engine reads only how many. */
  trainingDays: z.array(z.enum(WEEKDAYS)).max(7).default([]),
  split: z.enum(TrainingSplit).default(TrainingSplit.RECOVERY),
  variety: z.enum(TrainingVariety).default(TrainingVariety.BALANCED),
  repScheme: z.enum(RepScheme).default(RepScheme.STRAIGHT),
  compoundSets: range(1, 8).default({ min: 3, max: 4 }),
  isolationSets: range(1, 8).default({ min: 2, max: 3 }),
  compoundReps: range(1, 30).default({ min: 6, max: 10 }),
  isolationReps: range(1, 30).default({ min: 10, max: 15 }),
  focusMuscles: z.array(z.enum(Muscle)).max(2).default([]),
  excludedMuscles: z.array(z.enum(Muscle)).max(4).default([]),
  /** Free text the trainer and the AI assistant read; the engine does not. */
  injuries: z.string().max(1000).default(""),
  compoundWarmupSets: z.number().int().min(0).max(4).default(2),
  isolationWarmupSets: z.number().int().min(0).max(4).default(0),
  stretching: z.boolean().default(false),
  supersets: z.boolean().default(false),
  dropSets: z.boolean().default(false),
  compoundRestSeconds: z.number().int().min(15).max(600).default(120),
  isolationRestSeconds: z.number().int().min(15).max(600).default(90),
  defaultGymId: z.uuid().nullable().default(null),
  /** Sets the athlete already knows they can do. They calibrate first weights; history overrides them. */
  knownLifts: z
    .array(knownLiftSchema)
    .max(Object.values(KeyLift).length)
    .refine((lifts) => new Set(lifts.map((known) => known.lift)).size === lifts.length, {
      message: "one known lift per key lift",
    })
    .default([]),
});
export type TrainingQuestionnaire = z.infer<typeof trainingQuestionnaireSchema>;

/** The diet questionnaire, shaped after what a dietitian asks before writing a plan. */
export const dietQuestionnaireSchema = z.object({
  goal: z.enum(DietGoal).default(DietGoal.MAINTAIN),
  /** Kilograms a week the athlete wants to lose or gain. Ignored when maintaining. */
  weeklyRateKg: z.number().min(0).max(1).default(0.5),
  targetWeightKg: z.number().min(30).max(300).nullable().default(null),
  activity: z.enum(ActivityLevel).default(ActivityLevel.MODERATE),
  mealsPerDay: z.number().int().min(2).max(6).default(4),
  meals: z
    .array(z.object({ slot: z.enum(MealSlot), time: z.string().regex(/^\d{2}:\d{2}$/) }))
    .max(6)
    .default([]),
  dietType: z.enum(DietType).default(DietType.STANDARD),
  allergens: z.array(z.enum(Allergen)).default([]),
  /** Foods the athlete will not eat, in their own words ("seler", "wątróbka"). */
  dislikedFoods: z.array(z.string().min(1).max(60)).max(50).default([]),
  likedFoods: z.array(z.string().min(1).max(60)).max(50).default([]),
  shops: z.array(z.enum(Shop)).default([]),
  /** PLN a week for the athlete's own food. Null means no limit. */
  weeklyBudget: z.number().int().min(50).max(5000).nullable().default(null),
  /** Longest the athlete wants to spend preparing one meal. */
  maxPrepMinutes: z.number().int().min(5).max(120).default(30),
  /** Cook lunch or dinner for 2–3 days at once. */
  batchCooking: z.boolean().default(true),
  /** Eats away from home on workdays and needs meals that travel. */
  mealsToGo: z.boolean().default(false),
  kitchen: z.array(z.enum(KitchenEquipment)).default([]),
  householdSize: z.number().int().min(1).max(10).default(1),
  healthNotes: z.string().max(2000).default(""),
  supplements: z.string().max(1000).default(""),
});
export type DietQuestionnaire = z.infer<typeof dietQuestionnaireSchema>;

/**
 * The rep ranges a goal starts from: strength works heavy and low, muscle in the middle, the rest
 * light and high. Choosing a goal in the questionnaire fills these in; the athlete can still edit them.
 */
export const GOAL_REP_RANGES: Record<TrainingGoal, Pick<TrainingQuestionnaire, "compoundReps" | "isolationReps">> = {
  [TrainingGoal.STRONGER]: { compoundReps: { min: 3, max: 6 }, isolationReps: { min: 6, max: 10 } },
  [TrainingGoal.MUSCLE]: { compoundReps: { min: 6, max: 10 }, isolationReps: { min: 10, max: 15 } },
  [TrainingGoal.LEAN]: { compoundReps: { min: 8, max: 12 }, isolationReps: { min: 12, max: 15 } },
  [TrainingGoal.GENERAL_FITNESS]: { compoundReps: { min: 8, max: 12 }, isolationReps: { min: 12, max: 15 } },
};

export const questionnaireSchemas = {
  [QuestionnaireKind.TRAINING]: trainingQuestionnaireSchema,
  [QuestionnaireKind.DIET]: dietQuestionnaireSchema,
} as const;
